import cx from 'classnames'
import React, { ChangeEvent, useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useAction } from 'hooks'

import { TranslationContext } from 'contexts/TranslationContext'
import { ReactComponent as RecIcon } from 'assets/timeline/live_stream.svg'

import Button from 'components/base/Button'
import InfoView from 'components/views/InfoView/InfoView'
import InputLiveUrl from './InputLiveUrl'

import { CircularProgress } from '../../base/CircularProgress/CircularProgress'
import {
  importLiveStreamFromUrl,
  startLiveCapture,
  stopLiveCapture,
  removeLiveStream, getLastCapturedURL
} from '../../../actions/liveCapture'
import { setPreviewLoading } from '../../../actions/preview'
import { selectRecordingStatus } from '../../../selectors/recording'
import styles from './live-stream.module.scss'

type Props = {
  onClose: () => void
}

export default function LiveStream({ onClose }: Props) {
  const { t } = useContext(TranslationContext)
  const setLoadingSource = useAction(setPreviewLoading)
  const importLiveStream = useAction(importLiveStreamFromUrl)
  const startCapture = useAction(startLiveCapture)
  const stopCapture = useAction(stopLiveCapture)
  const deleteCapture = useAction(removeLiveStream)
  const recordingStatus = useSelector(selectRecordingStatus)

  const [ importing, setImporting ] = React.useState(false)
  const [ capturing, setCapturing ] = React.useState(false)
  const [ sourceUrl, setSourceUrl ] = React.useState(getLastCapturedURL())

  useEffect(() => {
    setCapturing(recordingStatus === 'capture')

    if (recordingStatus === 'capture') {
      setImporting(false)
    }
  }, [ recordingStatus ])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stopCapture(), [])

  const clearInput = () => {
    setSourceUrl('')
  }
  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => setSourceUrl(e.target.value)
  const onInputSubmit = async () => {
    if (!capturing && !importing) {
      const url = sourceUrl.trim()
      setSourceUrl(url)

      if (url) {
        setLoadingSource(true)
        setImporting(true)

        importLiveStream(url)
          .then((id: string) => {
            if (id) {
              startCapture(id)
            } else {
              reset()
            }
          })
          .catch(reset)
      }
    }
  }

  const reset = () => {
    setLoadingSource(false)
    setImporting(false)
    setCapturing(false)
  }

  const handleStop = () => {
    if (capturing) {
      stopCapture().then(reset)
    }
  }

  const handleDelete = () => {
    deleteCapture().then(reset)
  }

  const controls = (() => {
    switch (recordingStatus) {
      case 'stop':
      case 'error':
      case 'saving':
        return (
          <Button
            className={cx(styles.button, styles.start)}
            onClick={onInputSubmit}
            classes={{ startIcon: styles.startIcon }}
            startIcon={<RecIcon className={cx(styles.icon)} />}
            disabled={!sourceUrl || importing}
            primary
            title={t(importing ? 'LIVE_STREAM_BTN_CONNECTING_TOOLTIP' : 'LIVE_STREAM_BTN_CAPTURE_TOOLTIP')}
            data-lang-id="LIVE_STREAM_BTN_CAPTURE"
          >
            {t(importing ? 'LIVE_STREAM_BTN_CONNECTING' : 'LIVE_STREAM_BTN_CAPTURE')}
          </Button>
        )
      default:
        return (
          <>
            <Button
              className={cx(styles.button, styles.delete)}
              onClick={handleDelete}
              startIcon={<div className={cx(styles.icon, styles.deleteIcon)} />}
              title={t('VOICE_OVER_BTN_DELETE_TOOLTIP')}
              data-lang-id="VOICE_OVER_BTN_DELETE"
            />
            <Button
              className={cx(styles.button, styles.stop)}
              startIcon={<div className={cx(styles.icon, styles.stopIcon)} />}
              onClick={handleStop}
              primary
              title={t('LIVE_STREAM_BTN_CAPTURE_STOP_TOOLTIP')}
              data-lang-id="LIVE_STREAM_BTN_CAPTURE_STOP"
            >
              {t('LIVE_STREAM_BTN_CAPTURE_STOP')}
            </Button>
          </>
        )
    }
  })()

  return (
    <div className={styles.container}>
      <InfoView header={t('LIVE_STREAM_TEXT_RECORD_STREAM')} className={styles.infoView} onHide={onClose}>
        <div className={cx(styles.input, { [styles.disabled]: capturing })}>
          <div className={styles.label}>{t('LIVE_STREAM_URL_INPUT_LABEL')}</div>
          <InputLiveUrl
            placeholder={t('LIVE_STREAM_URL_INPUT_PLACEHOLDER')}
            onChange={onInputChange}
            onEnterKeyDown={onInputSubmit}
            value={sourceUrl}
            onClearInput={clearInput}
            disabled={capturing}
          />
        </div>
        {importing
          && (
            <div className={styles.processing}>
              <CircularProgress
                size={100}
                endless
                percents={50}
                transparent
                text={undefined}
                progress={0}
              />
            </div>
          )}
      </InfoView>
      <div className={styles.controls}>
        {controls}
      </div>
    </div>
  )
}
